import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Text,
  Heading,
  CardHeader,
  SearchInput,
  Dropdown,
  DropdownItem,
  SideBySideListBuilder,
  Popover,
  Tree,
  Flex,
  Box,
  TextButton,
  IconButton,
} from '@amzn/storm-ui';
import type {
  AvailableItemsTab,
  ViewOptionsHelpers,
  RenderItemFunction,
  TabRenderHelpers,
} from '@amzn/storm-ui';
import {
  useLazyGetChooseAccountsContextQuery,
  useLazyGetManagerAccountChildrenQuery,
} from 'src/shared/services/combine-accounts/combineAccounts';
import {
  EligibleAccount,
  CombineAccountType,
} from 'src/shared/services/combine-accounts/types';
import useIntl from '@i18n/useIntl';
import {
  COMBINE_ACCOUNTS_ERROR_DUPLICATE_DSP_KEY,
  COMBINE_ACCOUNTS_ERROR_DUPLICATE_SA_KEY,
  COMBINE_ACCOUNTS_LOADING_KEY,
  COMBINE_ACCOUNTS_LOAD_MORE_KEY,
  COMBINE_ACCOUNTS_LOAD_MORE_ACCOUNTS_KEY,
  CHOOSE_ACCOUNTS_HEADING_KEY,
  CHOOSE_ACCOUNTS_DESCRIPTION_KEY,
  CHOOSE_ACCOUNTS_NO_ITEMS_SELECTED_KEY,
  CHOOSE_ACCOUNTS_ADD_KEY,
  CHOOSE_ACCOUNTS_ADDED_KEY,
  CHOOSE_ACCOUNTS_REMOVE_KEY,
  CHOOSE_ACCOUNTS_REMOVE_ALL_KEY,
  CHOOSE_ACCOUNTS_REMOVE_ACCOUNT_KEY,
  CHOOSE_ACCOUNTS_ADD_ALL_KEY,
  CHOOSE_ACCOUNTS_TOGGLE_KEY,
  CHOOSE_ACCOUNTS_GROUP_HEADER_DSP_KEY,
  CHOOSE_ACCOUNTS_GROUP_HEADER_SA_KEY,
  CHOOSE_ACCOUNTS_SELECTED_WITH_MAX_KEY,
  CHOOSE_ACCOUNTS_SEARCH_KEY,
  CHOOSE_ACCOUNTS_FILTER_BY_KEY,
  CHOOSE_ACCOUNTS_TAB_YOUR_ACCOUNTS_KEY,
  CHOOSE_ACCOUNTS_TAB_REQUEST_ACCESS_KEY,
} from '@features/combine-accounts/constants';
import {
  SearchFilterRow,
  FlexOne,
  AccountTypeGroupHeader,
  PopoverContent,
} from '../style';
import {
  AccountItem,
  FilterKey,
  FILTER_OPTIONS,
  DEFAULT_PAGE_SIZE,
  MA_CHILDREN_PAGE_SIZE,
  FILTER_KEY_ALL,
  FILTER_KEY_SA,
  FILTER_KEY_DSP,
  FILTER_KEY_TO_AD_PRODUCT_GROUP,
  toItem,
  toChildItem,
} from '../types';
import RequestAccessTab from './RequestAccessTab';

/* Puff keys for the selected-panel group headers, resolved through intl at
   render time. These were hardcoded English until 2026-08-20, so the headers
   rendered untranslated in all 27 locales; the map's own keys stay the code's
   vocabulary so grouping logic is unaffected by translation. Plural per the
   approved upgrade frames, which read "AMAZON DSP ACCOUNTS". */
const ACCOUNT_TYPE_GROUP_HEADER_KEYS: Record<string, string> = {
  [CombineAccountType.SA]: CHOOSE_ACCOUNTS_GROUP_HEADER_SA_KEY,
  [CombineAccountType.DSP]: CHOOSE_ACCOUNTS_GROUP_HEADER_DSP_KEY,
};

const TYPE_ORDER: CombineAccountType[] = [
  CombineAccountType.DSP,
  CombineAccountType.SA,
];

const INITIAL_FILTERS: Record<string, unknown> = {
  accountType: FILTER_KEY_ALL,
  searchQuery: '',
};

const matchesSearch = (item: AccountItem, query: string): boolean => {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    item.label.toLowerCase().includes(q) ||
    String(item.id).toLowerCase().includes(q)
  );
};

/** True when a type filter is active and excludes this item's type.
 *
 *  Manager accounts are never excluded directly, they are judged by their
 *  children, and the synthetic load-more row is typed MA so it always survives.
 */
const isExcludedByTypeFilter = (
  item: AccountItem,
  filterBy: FilterKey,
): boolean => {
  if (filterBy === FILTER_KEY_SA)
    return item.accountType === CombineAccountType.DSP;
  if (filterBy === FILTER_KEY_DSP)
    return item.accountType === CombineAccountType.SA;
  // FILTER_KEY_ALL, and any key added later, excludes nothing until taught to.
  return false;
};

const filterAccountsByTypeAndSearch = (
  items: AccountItem[],
  filters: Record<string, unknown>,
): AccountItem[] => {
  const filterBy = (filters.accountType as FilterKey) || FILTER_KEY_ALL;
  const query = ((filters.searchQuery as string) || '').trim();

  return items.reduce<AccountItem[]>((kept, item) => {
    if (item.children) {
      /*
        Children are filtered too, not just the manager account row. Judging the
        row alone kept the whole group and then rendered every child in it, so a
        DSP filter still surfaced Sponsored ads accounts one level down, and
        "Add all" still offered them. Identity is preserved when no type filter
        is active so the unfiltered path hands Storm the same object it had.
      */
      const children =
        filterBy === FILTER_KEY_ALL
          ? item.children
          : item.children.filter(
              (child) => !isExcludedByTypeFilter(child, filterBy),
            );

      /*
        A group is dropped only when we know it has nothing to offer. If its
        load-more row is present there are children we have not fetched yet, so
        it stays: dropping it would hide the only control that could load a
        matching child, making those accounts unreachable. The cost is a group
        that expands to just "Load more", which is recoverable; hiding reachable
        accounts is not.
      */
      const hasRemainingAccount = children.some(
        (child) => !String(child.id).startsWith('load-more-'),
      );
      const mayHaveUnloadedChildren = children.some((child) =>
        String(child.id).startsWith('load-more-'),
      );
      if (
        filterBy !== FILTER_KEY_ALL &&
        !hasRemainingAccount &&
        !mayHaveUnloadedChildren
      )
        return kept;

      if (query) {
        const maMatches = matchesSearch(item, query);
        const childMatches = children.some((child) =>
          matchesSearch(child, query),
        );
        if (!maMatches && !childMatches) return kept;
      }

      kept.push(children === item.children ? item : { ...item, children });
      return kept;
    }

    if (isExcludedByTypeFilter(item, filterBy)) return kept;
    if (query && !matchesSearch(item, query)) return kept;
    kept.push(item);
    return kept;
  }, []);
};

interface ChooseAccountsStepProps {
  selectedItems: AccountItem[];
  onSelectionChange: (items: AccountItem[]) => void;
  onAddRequestAccessItem: (item: AccountItem) => void;
  errorAlert: { message: string; targetId: string } | null;
  onError: (error: { message: string; targetId: string }) => void;
  onClearError: () => void;
  maxSelections?: number;
  heading?: string;
  description?: string;
  noItemsSelectedLabel?: string;
  labelsSelected?: string;
  allowMultipleSameType?: boolean;
  /** Hides the "Request access" tab. Defaults to false (tab shown). */
  hideRequestAccessTab?: boolean;
  /** Hides the "Filter by" account type dropdown. Defaults to false (shown). */
  hideFilterBy?: boolean;
  /**
   * Shows an "Add all" link on each manager account row, adding every eligible
   * account beneath it in one click. Defaults to false.
   *
   * Opt-in rather than always on because it only makes sense without a
   * selection cap. The combine lane caps at two accounts, where "add all" would
   * either exceed the cap or silently add an arbitrary subset; the upgrade lane
   * sets no cap, which is where the approved design places the control.
   */
  allowAddAll?: boolean;
  /**
   * Restricts the list to a single account type. Defaults to no restriction.
   *
   * Applied to the item list handed to the builder, not through the "Filter by"
   * dropdown's initial value: with tabs configured, Storm reads
   * viewOptions.initialFilters for the dropdown's own state but only ever
   * filters items once its onFiltersChange fires, so an initial filter shows the
   * right dropdown label over a completely unfiltered list. Restricting the
   * source list also means "Add all" and the selected panel inherit it, rather
   * than each needing its own type check.
   */
  restrictToAccountType?: FilterKey;
}

/** Converts flat account list + MA children map into a tree of AccountItems.
 *  Deduplicates accounts that appear both as standalone and as MA children. */
const buildAccountTreeItems = (
  accounts: EligibleAccount[],
  maChildren: Record<string, EligibleAccount[]>,
  childrenNextTokens: Record<string, string | null>,
): AccountItem[] => {
  if (!accounts) return [];

  // Collect all child account IDs so we can skip standalone duplicates
  const childIds = new Set<string>();
  for (const children of Object.values(maChildren)) {
    for (const c of children) {
      childIds.add(c.accountId);
    }
  }

  const seen = new Set<string>();
  const items: AccountItem[] = [];
  accounts.forEach((a) => {
    if (seen.has(a.accountId)) return;
    seen.add(a.accountId);

    if (a.accountType === CombineAccountType.MA) {
      const loaded = maChildren[a.accountId];
      const children: AccountItem[] = loaded
        ? loaded.map((c) => toChildItem(c, a.accountId))
        : [];
      if (childrenNextTokens[a.accountId] && children.length > 0) {
        children.push({
          id: `load-more-${a.accountId}`,
          label: '',
          accountType: CombineAccountType.MA,
          parentManagerAccountId: a.accountId,
        } as AccountItem);
      }
      items.push({
        ...toItem(a),
        ...(children.length > 0 && { children }),
      });
    } else if (!childIds.has(a.accountId)) {
      items.push(toItem(a));
    }
  });
  return items;
};

const ChooseAccountsStep: React.FC<ChooseAccountsStepProps> = ({
  selectedItems,
  onSelectionChange,
  onAddRequestAccessItem,
  errorAlert,
  onError,
  onClearError,
  maxSelections,
  heading,
  description,
  noItemsSelectedLabel,
  labelsSelected,
  allowMultipleSameType,
  hideRequestAccessTab,
  hideFilterBy,
  allowAddAll,
  restrictToAccountType,
}) => {
  const { intl } = useIntl();
  const [accounts, setAccounts] = useState<EligibleAccount[]>([]);
  const [isInitialContextLoaded, setIsInitialContextLoaded] = useState(false);
  const [maChildren, setMaChildren] = useState<
    Record<string, EligibleAccount[]>
  >({});

  // Pagination state for top-level accounts
  const [accountsNextToken, setAccountsNextToken] = useState<string | null>(
    null,
  );
  const [isLoadingMoreAccounts, setIsLoadingMoreAccounts] = useState(false);

  // Pagination state for MA children (per MA account)
  const [childrenNextTokens, setChildrenNextTokens] = useState<
    Record<string, string | null>
  >({});
  const [loadingChildrenFor, setLoadingChildrenFor] = useState<Set<string>>(
    new Set(),
  );

  // Selected "Filter by" type, mirrored from the Storm view-options dropdown
  // (below) so the server-side children fetch can read it. Maps to the
  // ad-product-group value sent to the BFF; undefined for "All".
  const [selectedAccountType, setSelectedAccountType] =
    useState<FilterKey>(FILTER_KEY_ALL);
  const currentAdProductGroup =
    FILTER_KEY_TO_AD_PRODUCT_GROUP[selectedAccountType];

  // Always holds the filter currently in effect. An in-flight load-more captures
  // the filter it started under and compares against this ref before writing, so
  // a filter change mid-request drops the now-stale results instead of merging
  // them (and a stale pagination token) into the freshly reset list.
  const currentAdProductGroupRef = useRef(currentAdProductGroup);
  useEffect(() => {
    currentAdProductGroupRef.current = currentAdProductGroup;
  }, [currentAdProductGroup]);

  const [triggerContext, { isFetching: isContextLoading }] =
    useLazyGetChooseAccountsContextQuery();
  const [triggerChildren] = useLazyGetManagerAccountChildrenQuery();

  // Load, and reload on filter change, the top-level context and each Manager
  // Account's first page of children for the selected filter. The BFF returns a
  // filter-specific set (only matching accounts plus MAs) and auto-follows empty
  // pages, so a filter change must refetch rather than re-filter what is loaded.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await triggerContext({
          maxResults: DEFAULT_PAGE_SIZE,
          ...(currentAdProductGroup !== undefined && {
            adProductGroup: currentAdProductGroup,
          }),
        }).unwrap();
        if (cancelled) return;
        setAccounts(r.accounts ?? []);
        setAccountsNextToken(r.paginationToken ?? null);
        setMaChildren({});
        setChildrenNextTokens({});
        const maAccounts = (r.accounts ?? []).filter(
          (a) => a.accountType === CombineAccountType.MA,
        );
        await Promise.all(
          maAccounts.map(async (ma) => {
            try {
              const cr = await triggerChildren({
                managerAccountId: ma.accountId,
                maxResults: MA_CHILDREN_PAGE_SIZE,
                ...(currentAdProductGroup !== undefined && {
                  adProductGroup: currentAdProductGroup,
                }),
              }).unwrap();
              if (!cancelled) {
                setMaChildren((prev) => ({
                  ...prev,
                  [ma.accountId]: cr.children,
                }));
                setChildrenNextTokens((prev) => ({
                  ...prev,
                  [ma.accountId]: cr.paginationToken ?? null,
                }));
              }
            } catch {
              if (!cancelled) {
                setMaChildren((prev) => ({ ...prev, [ma.accountId]: [] }));
              }
            }
          }),
        );
        if (!cancelled) setIsInitialContextLoaded(true);
      } catch {
        if (!cancelled) setIsInitialContextLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentAdProductGroup, triggerContext, triggerChildren]);

  // Ref guards to prevent duplicate requests on rapid clicks
  const isLoadingMoreAccountsRef = useRef(false);
  const loadingChildrenForRef = useRef(new Set<string>());

  const handleLoadMoreAccounts = useCallback(async () => {
    if (!accountsNextToken || isLoadingMoreAccountsRef.current) return;
    const requestedGroup = currentAdProductGroup;
    isLoadingMoreAccountsRef.current = true;
    setIsLoadingMoreAccounts(true);
    try {
      const r = await triggerContext({
        maxResults: DEFAULT_PAGE_SIZE,
        paginationToken: accountsNextToken,
        ...(currentAdProductGroup !== undefined && {
          adProductGroup: currentAdProductGroup,
        }),
      }).unwrap();
      // Filter changed while this page was loading; drop the stale result so it
      // isn't merged into the reset list (and doesn't overwrite the new token).
      if (currentAdProductGroupRef.current !== requestedGroup) return;
      const existingIds = new Set(accounts.map((a) => a.accountId));
      const newAccounts = (r.accounts ?? []).filter(
        (a) => !existingIds.has(a.accountId),
      );
      setAccounts((prev) => [...prev, ...newAccounts]);
      setAccountsNextToken(r.paginationToken ?? null);
      // Load children for any new MA accounts
      const newMAs = newAccounts.filter(
        (a) => a.accountType === CombineAccountType.MA,
      );
      await Promise.all(
        newMAs.map(async (ma) => {
          try {
            const cr = await triggerChildren({
              managerAccountId: ma.accountId,
              maxResults: MA_CHILDREN_PAGE_SIZE,
              adProductGroup: currentAdProductGroup,
            }).unwrap();
            setMaChildren((prev) => ({
              ...prev,
              [ma.accountId]: cr.children,
            }));
            setChildrenNextTokens((prev) => ({
              ...prev,
              [ma.accountId]: cr.paginationToken ?? null,
            }));
          } catch {
            setMaChildren((prev) => ({ ...prev, [ma.accountId]: [] }));
          }
        }),
      );
    } catch {
      // Error loading more accounts — spinner dismissed via finally
    } finally {
      isLoadingMoreAccountsRef.current = false;
      setIsLoadingMoreAccounts(false);
    }
  }, [
    accounts,
    accountsNextToken,
    triggerContext,
    triggerChildren,
    currentAdProductGroup,
  ]);

  const handleLoadMoreChildren = useCallback(
    async (managerAccountId: string) => {
      const token = childrenNextTokens[managerAccountId];
      if (!token || loadingChildrenForRef.current.has(managerAccountId)) return;
      const requestedGroup = currentAdProductGroup;
      loadingChildrenForRef.current.add(managerAccountId);
      setLoadingChildrenFor((prev) => new Set(prev).add(managerAccountId));
      try {
        const cr = await triggerChildren({
          managerAccountId,
          maxResults: MA_CHILDREN_PAGE_SIZE,
          paginationToken: token,
          adProductGroup: currentAdProductGroup,
        }).unwrap();
        if (currentAdProductGroupRef.current !== requestedGroup) return;
        setMaChildren((prev) => {
          const existing = prev[managerAccountId] ?? [];
          const existingIds = new Set(existing.map((c) => c.accountId));
          const newChildren = cr.children.filter(
            (c) => !existingIds.has(c.accountId),
          );
          return { ...prev, [managerAccountId]: [...existing, ...newChildren] };
        });
        setChildrenNextTokens((prev) => ({
          ...prev,
          [managerAccountId]: cr.paginationToken ?? null,
        }));
      } catch {
        // Error loading more children — spinner dismissed via finally
      } finally {
        loadingChildrenForRef.current.delete(managerAccountId);
        setLoadingChildrenFor((prev) => {
          const next = new Set(prev);
          next.delete(managerAccountId);
          return next;
        });
      }
    },
    [childrenNextTokens, triggerChildren, currentAdProductGroup],
  );

  const treeItems = useMemo(
    () => buildAccountTreeItems(accounts, maChildren, childrenNextTokens),
    [accounts, maChildren, childrenNextTokens],
  );

  /** Enrich items with error popovers when a same-type account is already selected. */
  const enrichedTreeItems = useMemo(() => {
    if (allowMultipleSameType) return treeItems;
    const selectedTypes = new Set(selectedItems.map((i) => i.accountType));
    const addError = (item: AccountItem): AccountItem => {
      const enrichedChildren = item.children
        ? item.children.map(addError)
        : undefined;

      if (
        selectedTypes.has(item.accountType) &&
        !selectedItems.some((selected) => selected.id === item.id)
      ) {
        const errorKey =
          item.accountType === CombineAccountType.DSP
            ? COMBINE_ACCOUNTS_ERROR_DUPLICATE_DSP_KEY
            : COMBINE_ACCOUNTS_ERROR_DUPLICATE_SA_KEY;
        return {
          ...item,
          ...(enrichedChildren && { children: enrichedChildren }),
          error: {
            message: intl.getMessage(errorKey),
            type: 'error' as const,
          },
        };
      }

      return enrichedChildren ? { ...item, children: enrichedChildren } : item;
    };
    return treeItems.map(addError);
  }, [treeItems, selectedItems, intl, allowMultipleSameType]);

  const renderSelectedItem = useCallback(
    (item: AccountItem) => (
      <div>
        <Text>{item.label}</Text>
        <Text size="small" color="secondary">
          {item.id}
        </Text>
      </div>
    ),
    [],
  );

  /**
   * The list this lane is allowed to offer.
   *
   * Reuses the type half of the dropdown's own filter, so a restricted lane and
   * a user-chosen filter behave identically, including dropping Sponsored ads
   * children from a manager account group rather than only hiding whole groups.
   * Everything downstream reads this, so "Add all" and the selected panel
   * inherit the restriction for free.
   */
  const visibleTreeItems = useMemo(
    () =>
      restrictToAccountType && restrictToAccountType !== FILTER_KEY_ALL
        ? filterAccountsByTypeAndSearch(enrichedTreeItems, {
            accountType: restrictToAccountType,
          })
        : enrichedTreeItems,
    [enrichedTreeItems, restrictToAccountType],
  );

  const [popoverItemId, setPopoverItemId] = useState<string | number | null>(
    null,
  );
  const popoverTriggerRefs = useRef<Record<string, HTMLDivElement | null>>({});

  /** Flattened list of all enriched items (parents + children) for popover lookups. */
  const allFlatItems = useMemo(
    () =>
      ([] as AccountItem[]).concat(
        ...visibleTreeItems.map((item) =>
          item.children ? [item, ...item.children] : [item],
        ),
      ),
    [visibleTreeItems],
  );

  /** Memoized trigger ref so the Popover doesn't re-mount on every render. */
  const popoverTriggerRef = useMemo(
    () => ({
      current:
        popoverItemId != null
          ? popoverTriggerRefs.current[String(popoverItemId)]
          : null,
    }),
    [popoverItemId],
  );

  // Close popover when the conflict is resolved (e.g. user removes the selected account)
  useEffect(() => {
    if (popoverItemId == null) return;
    const item = allFlatItems.find((flatItem) => flatItem.id === popoverItemId);
    if (!item?.error) {
      setPopoverItemId(null);
    }
  }, [allFlatItems, popoverItemId]);

  const renderAvailableItem = useCallback(
    (item: AccountItem) => (
      <div
        ref={(el) => {
          popoverTriggerRefs.current[String(item.id)] = el;
        }}
      >
        <Text size="small">{item.label}</Text>
        {item.description && (
          <Text size="xsmall" color="tertiary">
            {item.description}
          </Text>
        )}
      </div>
    ),
    [],
  );

  /**
   * Custom render for the "Your accounts" tab.
   * Uses the tab's render callback so we fully control the Add button —
   * conflicting items show a popover instead of being added.
   */
  const renderYourAccountsTab = useCallback(
    (helpers: TabRenderHelpers<AccountItem>, filteredItems: AccountItem[]) => {
      const { isSelected, canAddMore, renderItem } = helpers;

      const handleAdd = (item: AccountItem) => {
        if (item.error?.type === 'error') {
          setPopoverItemId(item.id);
          return;
        }
        helpers.onAdd(item);
      };

      const renderNode = (node: AccountItem) => {
        // Synthetic load-more item
        if (String(node.id).startsWith('load-more-')) {
          return (
            <Box padding="xxsmall">
              <TextButton
                onClick={() =>
                  handleLoadMoreChildren(node.parentManagerAccountId!)
                }
                disabled={loadingChildrenFor.has(node.parentManagerAccountId!)}
              >
                {loadingChildrenFor.has(node.parentManagerAccountId!)
                  ? intl.getMessage(COMBINE_ACCOUNTS_LOADING_KEY)
                  : intl.getMessage(COMBINE_ACCOUNTS_LOAD_MORE_KEY)}
              </TextButton>
            </Box>
          );
        }

        const nodeSelected = isSelected(node);
        const isManagerAccount = node.accountType === CombineAccountType.MA;
        const isIneligible = node.eligibilityStatus?.isEligible === false;

        /*
          Children this "Add all" would actually add. Excluded: the synthetic
          load-more row, which is not an account; ineligible accounts, which
          have no Add control of their own either; rows carrying a conflict
          error, so Add all can never add something the per-row Add would have
          refused and explained in a popover; and anything already selected, so
          the button reflects work left to do rather than the group size.

          Account type needs no check here. node.children comes from
          visibleTreeItems, which already applies restrictToAccountType, so on
          the upgrade lane these are Amazon DSP accounts by construction.
        */
        const addableChildren = allowAddAll
          ? (node.children ?? []).filter(
              (child) =>
                !String(child.id).startsWith('load-more-') &&
                child.eligibilityStatus?.isEligible !== false &&
                child.error?.type !== 'error' &&
                !isSelected(child),
            )
          : [];

        return (
          <Flex
            flex={1}
            gap="small"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            style={isIneligible ? { opacity: 0.5 } : undefined}
          >
            {/*
              minWidth: 0 lets this column shrink below the intrinsic width of
              its content. Long unbroken account names such as
              "GlobalAccount-openRegistration:amzn1.account.ocid.A1ARQFSORKZC67"
              otherwise refuse to shrink and push the status column past the
              panel edge, where it renders clipped (SGA-QA-Bug-592).
            */}
            <Box
              flex={1}
              padding="none"
              style={{ minWidth: 0 }}
              data-testid="account-row-content"
            >
              {renderItem(node)}
            </Box>
            {isManagerAccount && allowAddAll && (
              // Same never-shrinking right-aligned column as the per-account
              // status below, so "Add all" lines up with each child's "Add".
              <Box
                padding="none"
                style={{ flexShrink: 0, textAlign: 'right' }}
                data-testid="account-row-add-all"
              >
                <TextButton
                  disabled={!canAddMore || addableChildren.length === 0}
                  onClick={() => helpers.onAddMultiple(addableChildren)}
                >
                  {intl.getMessage(CHOOSE_ACCOUNTS_ADD_ALL_KEY)}
                </TextButton>
              </Box>
            )}
            {!isManagerAccount && !isIneligible && (
              // Own column, never shrinking, right-aligned, so "Add" and
              // "Added" keep one right edge regardless of name length.
              <Box
                padding="none"
                style={{ flexShrink: 0, textAlign: 'right' }}
                data-testid="account-row-status"
              >
                {nodeSelected ? (
                  <Text color="tertiary">
                    {intl.getMessage(CHOOSE_ACCOUNTS_ADDED_KEY)}
                  </Text>
                ) : (
                  <TextButton
                    /*
                      The selection cap wins over the conflict popover.
                      enrichedTreeItems sets an error on every unselected
                      account whose type is already selected, so at 2/2 every
                      remaining row carries one. The previous condition
                      "!canAddMore && !node.error" therefore evaluated to false
                      for all of them and left every Add button enabled
                      (SGA-QA-Bug-594). Below the cap canAddMore is true, so
                      conflicting rows stay clickable and still open the
                      explanatory popover via handleAdd.
                    */
                    disabled={!canAddMore}
                    onClick={() => handleAdd(node)}
                  >
                    {intl.getMessage(CHOOSE_ACCOUNTS_ADD_KEY)}
                  </TextButton>
                )}
              </Box>
            )}
          </Flex>
        );
      };

      return (
        <Box padding="none" flex={1}>
          <Tree
            data={filteredItems}
            role="tree"
            arrowLabel={intl.getMessage(CHOOSE_ACCOUNTS_TOGGLE_KEY)}
            renderNode={renderNode}
          />
          {accountsNextToken && (
            <Box padding="small">
              <TextButton
                onClick={handleLoadMoreAccounts}
                disabled={isLoadingMoreAccounts}
              >
                {isLoadingMoreAccounts
                  ? intl.getMessage(COMBINE_ACCOUNTS_LOADING_KEY)
                  : intl.getMessage(COMBINE_ACCOUNTS_LOAD_MORE_ACCOUNTS_KEY)}
              </TextButton>
            </Box>
          )}
        </Box>
      );
    },
    [
      accountsNextToken,
      isLoadingMoreAccounts,
      handleLoadMoreAccounts,
      loadingChildrenFor,
      handleLoadMoreChildren,
      childrenNextTokens,
      allowAddAll,
      intl,
    ],
  );

  const renderSelectedList = useCallback(
    (
      items: AccountItem[],
      renderItem: RenderItemFunction<AccountItem>,
      onRemove: (item: AccountItem) => void,
    ) => (
      <>
        {TYPE_ORDER.map((type) => {
          const itemsOfType = items.filter((i) => i.accountType === type);
          if (!itemsOfType.length) return null;
          return (
            <div key={type}>
              <AccountTypeGroupHeader>
                {intl.getMessage(ACCOUNT_TYPE_GROUP_HEADER_KEYS[type])}
              </AccountTypeGroupHeader>
              {itemsOfType.map((item) => {
                const removeLabel = intl.formatMessage(
                  CHOOSE_ACCOUNTS_REMOVE_ACCOUNT_KEY,
                  { account: item.label },
                );
                return (
                  <Flex
                    key={item.id}
                    alignItems="center"
                    justifyContent="space-between"
                    style={{ padding: '8px 12px' }}
                  >
                    <Box flex={1} padding="none">
                      {renderItem(item)}
                    </Box>
                    <IconButton
                      icon="close"
                      onClick={() => onRemove(item)}
                      aria-label={removeLabel}
                      label={removeLabel}
                      variant="transparent"
                      small
                    />
                  </Flex>
                );
              })}
            </div>
          );
        })}
      </>
    ),
    [intl],
  );

  const renderViewOptions = useCallback(
    ({ filters, setFilters }: ViewOptionsHelpers<AccountItem>) => (
      <SearchFilterRow>
        <FlexOne>
          <SearchInput
            id="combine-accounts-search"
            placeholder={intl.getMessage(CHOOSE_ACCOUNTS_SEARCH_KEY)}
            label={intl.getMessage(CHOOSE_ACCOUNTS_SEARCH_KEY)}
            hideLabel
            value={(filters.searchQuery as string) || ''}
            onChange={(e) =>
              setFilters({ ...filters, searchQuery: e.target.value })
            }
            onClear={() => setFilters({ ...filters, searchQuery: '' })}
          />
        </FlexOne>
        {!hideFilterBy && (
          <Dropdown
            selectedValue={(filters.accountType as string) || 'all'}
            onChange={(v) => {
              setFilters({ ...filters, accountType: v });
              setSelectedAccountType(v as FilterKey);
            }}
            label={intl.getMessage(CHOOSE_ACCOUNTS_FILTER_BY_KEY)}
            hideLabel
            selectedLabelOverride={intl.getMessage(
              CHOOSE_ACCOUNTS_FILTER_BY_KEY,
            )}
            inline
            transparentButton
          >
            {Object.entries(FILTER_OPTIONS).map(([k, v]) => (
              <DropdownItem key={k} value={k}>
                {intl.getMessage(v)}
              </DropdownItem>
            ))}
          </Dropdown>
        )}
      </SearchFilterRow>
    ),
    [hideFilterBy, intl],
  );

  const tabs: AvailableItemsTab<AccountItem>[] = useMemo(
    () => [
      {
        id: 'your-accounts',
        label: intl.getMessage(CHOOSE_ACCOUNTS_TAB_YOUR_ACCOUNTS_KEY),
        items: visibleTreeItems,
        viewOptions: {
          render: renderViewOptions,
          initialFilters: INITIAL_FILTERS,
          filterFunction: filterAccountsByTypeAndSearch,
        },
        renderItem: renderAvailableItem,
        render: renderYourAccountsTab,
      },
      ...(hideRequestAccessTab
        ? []
        : [
            {
              id: 'request-access',
              label: intl.getMessage(CHOOSE_ACCOUNTS_TAB_REQUEST_ACCESS_KEY),
              items: [],
              render: () => (
                <RequestAccessTab
                  selectedItems={selectedItems}
                  onAddItem={onAddRequestAccessItem}
                  errorAlert={errorAlert}
                  onError={onError}
                  onClearError={onClearError}
                />
              ),
            } as AvailableItemsTab<AccountItem>,
          ]),
    ],
    [
      visibleTreeItems,
      renderViewOptions,
      renderAvailableItem,
      renderYourAccountsTab,
      selectedItems,
      onAddRequestAccessItem,
      errorAlert,
      onError,
      onClearError,
      hideRequestAccessTab,
      intl,
    ],
  );

  return (
    <>
      <CardHeader paddingBlockEnd="base">
        <Heading renderAs="h1" size="xlarge">
          {heading ?? intl.getMessage(CHOOSE_ACCOUNTS_HEADING_KEY)}
        </Heading>
        <Text color="secondary">
          {description ?? intl.getMessage(CHOOSE_ACCOUNTS_DESCRIPTION_KEY)}
        </Text>
      </CardHeader>
      <SideBySideListBuilder<AccountItem>
        id="combine-accounts-builder"
        availableItems={visibleTreeItems}
        selectedItems={selectedItems}
        onSelected={onSelectionChange}
        maxSelections={maxSelections}
        loadingItems={isContextLoading && !isInitialContextLoaded}
        renderSelectedItem={renderSelectedItem}
        renderSelectedList={renderSelectedList}
        renderAvailableItem={renderAvailableItem}
        tabs={tabs}
        search={{ enabled: false }}
        labels={{
          ...(labelsSelected !== undefined && { selected: labelsSelected }),
          /*
            Storm renders labels.selected only when no maxSelections is set, and
            falls back to its own itemsSelectedWithMax label when one is. The
            combine lane always passes a max, so before this override it showed
            Storm's untranslated English "1/2 selected" in every locale.

            Formatted here rather than passed as a template because Storm's
            TranslationProvider returns an override verbatim and never
            interpolates values into it. maxSelections is only defined on the
            capped lane, which is the only lane that renders this label.
          */
          ...(maxSelections !== undefined && {
            itemsSelectedWithMax: intl.formatMessage(
              CHOOSE_ACCOUNTS_SELECTED_WITH_MAX_KEY,
              { count: selectedItems.length, max: maxSelections },
            ),
          }),
          noItemsSelected:
            noItemsSelectedLabel ??
            intl.getMessage(CHOOSE_ACCOUNTS_NO_ITEMS_SELECTED_KEY),
          add: intl.getMessage(CHOOSE_ACCOUNTS_ADD_KEY),
          remove: intl.getMessage(CHOOSE_ACCOUNTS_REMOVE_KEY),
          removeAll: intl.getMessage(CHOOSE_ACCOUNTS_REMOVE_ALL_KEY),
          ...(allowAddAll && {
            addAll: intl.getMessage(CHOOSE_ACCOUNTS_ADD_ALL_KEY),
          }),
        }}
      />
      {popoverItemId != null && popoverTriggerRef.current && (
        <Popover
          isOpen
          variant="inverted"
          withCloseButton
          onCloseButtonClick={() => {
            setPopoverItemId(null);
            onClearError();
          }}
          trigger={popoverTriggerRef as React.RefObject<HTMLElement>}
          position="right"
          padding="none"
        >
          <PopoverContent>
            <Text>
              {allFlatItems.find((item) => item.id === popoverItemId)?.error
                ?.message ?? ''}
            </Text>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
};

export default ChooseAccountsStep;
